// sanitize/logging.ts
// Safe logging utilities to prevent format string injection

import {
  DEFAULT_LOG_MAX_LENGTH,
  FORMAT_SPECIFIER_PATTERN
} from "./constants";
import type { SafeLogger, PrefixedLogger } from "./types";

/**
 * Sanitizes a string for safe logging (prevents format string injection)
 * Removes format specifiers like %s, %d, %i, %f, %j, %o, %O
 * @param value - String to sanitize
 * @param maxLength - Maximum length before truncation
 * @returns Sanitized string safe for logging
 */
export const sanitizeForLogging = (
  value: string | undefined | null,
  maxLength: number = DEFAULT_LOG_MAX_LENGTH
): string => {
  if (!value) return "[empty]";

  // Remove potential format specifiers that could cause issues
  let sanitized = value.replace(FORMAT_SPECIFIER_PATTERN, "[FILTERED]");

  // Truncate if needed
  if (sanitized.length > maxLength) {
    sanitized = sanitized.substring(0, maxLength) + "...[truncated]";
  }

  return sanitized;
};

/**
 * Sanitizes a URL specifically for logging purposes
 * @param url - URL to sanitize
 * @param maxLength - Maximum length before truncation
 * @returns Sanitized URL safe for logging
 */
export const sanitizeUrlForLogging = (
  url: string | undefined | null,
  maxLength: number = DEFAULT_LOG_MAX_LENGTH
): string => {
  return sanitizeForLogging(url, maxLength);
};

/**
 * Sanitizes an error message for logging
 * @param error - Error object to sanitize
 * @returns Sanitized error message
 */
export const sanitizeErrorForLogging = (
  error: Error | undefined | null
): string => {
  if (!error?.message) return "[unknown error]";
  return sanitizeForLogging(error.message);
};

/**
 * Sanitizes an object for logging by converting to JSON and sanitizing
 * @param obj - Object to sanitize
 * @param maxLength - Maximum length before truncation
 * @returns Sanitized JSON string
 */
export const sanitizeObjectForLogging = (
  obj: unknown,
  maxLength: number = DEFAULT_LOG_MAX_LENGTH
): string => {
  try {
    const json = JSON.stringify(obj);
    return sanitizeForLogging(json, maxLength);
  } catch {
    return "[unable to serialize]";
  }
};

/**
 * Safe logging wrapper that prevents format string injection
 * Uses explicit format specifiers to ensure user data cannot inject formatting
 */
export const safeLog: SafeLogger = {
  log: (prefix: string, message: string, ...args: unknown[]): void => {
    console.log("%s %s", prefix, message, ...args);
  },

  warn: (prefix: string, message: string, ...args: unknown[]): void => {
    console.warn("%s %s", prefix, message, ...args);
  },

  error: (prefix: string, message: string, ...args: unknown[]): void => {
    console.error("%s %s", prefix, message, ...args);
  },

  info: (prefix: string, message: string, ...args: unknown[]): void => {
    console.info("%s %s", prefix, message, ...args);
  },

  debug: (prefix: string, message: string, ...args: unknown[]): void => {
    console.debug("%s %s", prefix, message, ...args);
  },

  group: (prefix: string, label: string): void => {
    console.group("%s %s", prefix, label);
  },

  groupEnd: (): void => {
    console.groupEnd();
  }
};

/**
 * Creates a logger instance with a fixed prefix and optional enabled flag
 * @param prefix - Prefix to use for all log messages
 * @param enabled - Function that returns whether logging is enabled (defaults to always true)
 * @returns Logger instance with the prefix baked in
 */
export const createLogger = (
  prefix: string,
  enabled?: () => boolean
): PrefixedLogger => {
  const isEnabled = enabled ?? (() => true);

  return {
    log: (message?: string, ...args: unknown[]) => {
      if (!isEnabled()) return;
      if (message !== undefined) {
        safeLog.log(prefix, message, ...args);
      } else {
        console.log(prefix);
      }
    },
    warn: (message?: string, ...args: unknown[]) => {
      if (!isEnabled()) return;
      if (message !== undefined) {
        safeLog.warn(prefix, message, ...args);
      } else {
        console.warn(prefix);
      }
    },
    error: (message?: string, ...args: unknown[]) => {
      if (!isEnabled()) return;
      if (message !== undefined) {
        safeLog.error(prefix, message, ...args);
      } else {
        console.error(prefix);
      }
    },
    info: (message?: string, ...args: unknown[]) => {
      if (!isEnabled()) return;
      if (message !== undefined) {
        safeLog.info(prefix, message, ...args);
      } else {
        console.info(prefix);
      }
    },
    debug: (message?: string, ...args: unknown[]) => {
      if (!isEnabled()) return;
      if (message !== undefined) {
        safeLog.debug(prefix, message, ...args);
      } else {
        console.debug(prefix);
      }
    },
    group: (label?: string) => {
      if (!isEnabled()) return;
      if (label !== undefined) {
        safeLog.group(prefix, label);
      } else {
        console.group(prefix);
      }
    },
    groupEnd: () => {
      if (!isEnabled()) return;
      safeLog.groupEnd();
    },
    isEnabled
  };
};